import React, { useCallback, useMemo, useState } from 'react';
import { Modal, IndexTable, Text, Box, Checkbox, TextField } from '@shopify/polaris';
import { useProgramStore } from '#client/stores/program/useProgramStore';
export default function VariantsModal({ open, onClose, productId, mode }) {
    const [searchValue, setSearchValue] = useState('');
    const allProducts = useProgramStore.use.allProducts();
    const updateProductOverride = useProgramStore.use.updateProductOverride();
    // Build variants directly from allProducts for the selected product
    const variants = useMemo(() => {
        const product = allProducts.find((p) => p.id === productId);
        return product?.variants || [];
    }, [allProducts, productId]);

    const handleExcludeChange = (variantId, checked) => {
        updateProductOverride({ productId, variantId, exclude: checked });
    };

    const handleSave = async () => {
        onClose();
    };

    // handleSearchChange is used to update the search value for filtering variants
    const handleSearchChange = useCallback((value) => setSearchValue(value), []);

    // Filter variants by searchValue (SKU or title)
    const filteredVariants = useMemo(() => {
        if (!searchValue) return variants;
        const lower = searchValue.toLowerCase();
        return variants.filter((v) => (v.title && v.title.toLowerCase().includes(lower)) || (v.sku && v.sku.toLowerCase().includes(lower)));
    }, [variants, searchValue]);

    const rowMarkup = filteredVariants.map((variant, idx) => {
        const isOverrideChecked = !!variant.priceOverridden;
        const isExcludeChecked = !!variant.exclude;
        return (
            <IndexTable.Row id={variant.id} key={variant.id} position={idx}>
                <IndexTable.Cell>
                    <Text as="span">{variant.title}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span">{variant.sku}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Checkbox
                        label="Override price"
                        labelHidden
                        checked={isOverrideChecked}
                        onChange={(checked) => {
                            const fallback = variant.programPrice || variant.price || 0;
                            updateProductOverride({ productId, variantId: variant.id, priceOverridden: checked, programPrice: fallback });
                        }}
                        disabled={isExcludeChecked || mode === 'view'}
                    />
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <TextField
                        label="Override price"
                        value={variant.programPrice || variant.price || ''}
                        onChange={(value) => updateProductOverride({ productId, variantId: variant.id, priceOverridden: true, programPrice: value })}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        disabled={!isOverrideChecked || mode === 'view'}
                        labelHidden
                        prefix={'$'}
                    />
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Checkbox label="Exclude" labelHidden checked={isExcludeChecked} onChange={(checked) => handleExcludeChange(variant.id, checked)} disabled={isOverrideChecked || mode === 'view'} />
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Product Variants"
            primaryAction={mode === 'view' ? { content: 'Close', onAction: onClose } : { content: 'Save', onAction: handleSave }}
            secondaryActions={mode === 'view' ? [] : [{ content: 'Cancel', onAction: onClose }]}
            large
        >
            <Box padding="400">
                <>
                    <Box paddingBlockEnd="400">
                        <TextField label="Search variants" value={searchValue} onChange={handleSearchChange} placeholder="Search by SKU or name" autoComplete="off" labelHidden />
                    </Box>
                    <IndexTable
                        resourceName={{ singular: 'variant', plural: 'variants' }}
                        itemCount={filteredVariants.length}
                        headings={[{ title: 'Variant' }, { title: 'SKU' }, { title: 'Override' }, { title: 'Price' }, { title: 'Exclude' }]}
                        selectable={false}
                    >
                        {rowMarkup}
                    </IndexTable>
                </>
            </Box>
        </Modal>
    );
}
